import upperFirst from "lodash/upperFirst";

import {
  MedicalReviewState,
  OnboardingStepName,
  OrderedOnboardingSteps,
  TotalOnboardingSteps,
} from "@/constants/Onboarding";

interface OnboardingHeaderContent {
  counter?: string;
  subtitle: string;
}

const getStepSubtitles = (
  patientName?: string,
): Record<OnboardingStepName, string> => {
  const whose = patientName ? `${patientName}'s` : "your";
  const team = patientName ? "the admissions team" : "your admissions team";

  return {
    [OnboardingStepName.CareTeam]: `${upperFirst(team)} is ready to walk you through ${whose} provider options`,
    [OnboardingStepName.ClinicalPicture]: `${upperFirst(team)} is currently reviewing what you shared in ${whose} profile`,
    [OnboardingStepName.Initial]: `Once ${whose} profile is complete, ${team} can pick up where your calls left off`,
    [OnboardingStepName.Profile]: `Once ${whose} profile is complete, ${team} can pick up where your calls left off`,
    [OnboardingStepName.Treatment]: `A medical provider is reviewing ${whose} intake with ${team}`,
    [OnboardingStepName.YourWhy]: `${upperFirst(whose)} care team is confirmed. Your why is the part they can't learn from ${patientName ? "a" : "your"} chart — tell them before you meet`,
  };
};

const getReviewSubtitles = (
  patientName?: string,
): Record<MedicalReviewState, string> => {
  const whose = patientName ? `${patientName}'s` : "your";
  const team = patientName ? "the admissions team" : "your admissions team";
  const forWhom = patientName ?? "you";
  const forWhomSuffix = patientName ? ` for ${patientName}` : "";

  return {
    [MedicalReviewState.Approved]: `Medical review done. ${upperFirst(whose)} team is matching ${forWhom} with providers while you get a feel for treatment`,
    [MedicalReviewState.NotApproved]: `Onboarding is on hold, but you're not on your own — ${team} is reaching out to help you find the right care${forWhomSuffix}`,
    [MedicalReviewState.Pending]: `A medical provider is reviewing ${whose} intake with ${team}`,
  };
};

const getOnboardingHeaderTitle = (patientName?: string): string =>
  patientName
    ? `Before ${patientName}'s first visit`
    : "Before your first visit";

interface GetOnboardingHeaderContentParams {
  currentStepIndex: number;
  medicalReviewState?: MedicalReviewState;
  patientName?: string;
}

const getOnboardingHeaderContent = ({
  currentStepIndex,
  medicalReviewState,
  patientName,
}: GetOnboardingHeaderContentParams): OnboardingHeaderContent => {
  const stepsCompleted = currentStepIndex + 1;
  const counter = `${stepsCompleted} of ${TotalOnboardingSteps} done`;
  const whose = patientName ? `${patientName}'s` : "your";

  if (medicalReviewState) {
    return {
      counter:
        medicalReviewState === MedicalReviewState.NotApproved
          ? undefined
          : counter,
      subtitle: getReviewSubtitles(patientName)[medicalReviewState],
    };
  }

  const isComplete = currentStepIndex >= OrderedOnboardingSteps.length;

  if (isComplete) {
    return {
      counter,
      subtitle: `${upperFirst(whose)} care team is confirmed and ready for ${patientName ? "the" : "your"} first visit`,
    };
  }

  const currentStep = OrderedOnboardingSteps[currentStepIndex];

  return {
    counter,
    subtitle: getStepSubtitles(patientName)[currentStep],
  };
};

export { getOnboardingHeaderContent, getOnboardingHeaderTitle };
